import { TZDate } from '@date-fns/tz';
import { Trans } from '@lingui/react/macro';
import { BookingRecordListForCustomer } from '@navio/nsdk';
import { noddiColors, NoddiIcon } from 'noddi-ui-common';
import { NoddiBasicCard, NoddiDummyButton, PrettySlug } from 'noddi-ui-web';
import {
  BookingDetailsTabs,
  CustomDateFormats,
  customerRoutes,
  getCarDisplayName,
  NoddiDateFormats,
  noddiFormatDate
} from 'noddi-util';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import { BookingStatusChip } from '../Booking/BookingStatusChip';
import { CarManagerNames } from '../Common/CarManagerNames';

type BookingCardProps = {
  booking: BookingRecordListForCustomer;
  isUpcomingBooking?: boolean;
};

export const BookingCard = ({ booking, isUpcomingBooking }: BookingCardProps) => {
  const { currentBrand } = useCurrentBrand();
  const bookingTime = `${noddiFormatDate(new TZDate(booking.deliveryWindowStartsAt, booking.address.timezone.value), NoddiDateFormats.TIME)} - ${noddiFormatDate(new TZDate(booking.deliveryWindowEndsAt, booking.address.timezone.value), NoddiDateFormats.TIME)}`;
  const bookingDate = `${noddiFormatDate(
    new TZDate(booking.date, booking.address.timezone.value),
    CustomDateFormats.FULL_WEEKDAY_MONTH_AND_YEAR
  )}`;

  return (
    <NoddiBasicCard
      to={customerRoutes.bookingInfo.getPath({ slug: booking.slug, tab: BookingDetailsTabs.ORDER_DETAILS })}
      className='flex flex-col gap-4'
    >
      {currentBrand !== booking.serviceDepartmentBrand?.slug && (
        <div className='mb-4 flex justify-between gap-2'>
          <span className='text-muted-foreground'>
            <Trans>Services are delivered by</Trans>
          </span>
          {!!booking.serviceDepartmentBrand?.logo && (
            <div className='w-20 shrink-0'>
              <img src={booking.serviceDepartmentBrand.logo.url} alt={booking.serviceDepartmentBrand.name} />
            </div>
          )}
        </div>
      )}
      <div className='flex items-center justify-between gap-4'>
        <div className='flex flex-col'>
          <h6 className='text-xs'>
            <Trans>Booking reference</Trans>
          </h6>
          <h6 className='font-semibold md:text-lg'>
            <PrettySlug slug={booking.slug} />
          </h6>
        </div>
        {!isUpcomingBooking && <BookingStatusChip booking={booking} />}
      </div>

      <div className='minimal-scrollbar-thin flex max-h-48 flex-col gap-2 overflow-y-auto'>
        {booking.bookingItemsCar.map((bookingItem, bookingItemIdx) => (
          <div key={bookingItemIdx} className='flex flex-col gap-2'>
            <div className='font-semibold'>
              <p>{getCarDisplayName({ car: bookingItem, showYear: false })}</p>
            </div>
            <CarManagerNames carManagers={bookingItem.carManagers} />

            <div className='flex flex-wrap gap-x-2 text-sm text-muted-foreground'>
              {bookingItem.orderLines?.map(({ description }, serviceIndex) => (
                <span key={bookingItem.licensePlate.number + serviceIndex}>
                  {description}
                  {serviceIndex < (bookingItem?.orderLines?.length ?? 0) - 1 && ','}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
      {isUpcomingBooking && (
        <div className='flex flex-col gap-1'>
          {booking.hasCustomerVisit ? (
            <div className='flex items-baseline gap-2'>
              <div>
                <NoddiIcon color={noddiColors.black} name='Clock' />
              </div>
              <p>{bookingTime}</p>
            </div>
          ) : (
            <div className='flex items-baseline gap-2'>
              <div>
                <NoddiIcon color={noddiColors.black} name='Truck' />
              </div>
              <p>
                <Trans>We pick up your tires from your current tire hotel</Trans>
              </p>
            </div>
          )}
          <div className='flex items-baseline gap-2'>
            <div>
              <NoddiIcon color={noddiColors.black} name='Calendar' />
            </div>
            <p className='capitalize'>{bookingDate}</p>
          </div>
          <div className='flex items-baseline gap-2'>
            <div>
              <NoddiIcon name='MapPin' color={noddiColors.black} />
            </div>
            <p>
              {booking.address.streetName} {booking.address.streetNumber}
            </p>
          </div>
        </div>
      )}

      {!isUpcomingBooking && (
        <div className='flex items-baseline gap-2'>
          <div>
            <NoddiIcon color={noddiColors.black} name='Calendar' />
          </div>
          <p className='capitalize'>{bookingDate}</p>
        </div>
      )}

      <div className='flex items-end justify-end'>
        <NoddiDummyButton size='small' variant='link' className='-mr-4 -mb-2' endIcon='ArrowRight'>
          <Trans>See booking</Trans>
        </NoddiDummyButton>
      </div>
    </NoddiBasicCard>
  );
};
